import React, { ChangeEvent, DragEvent, useRef, useState } from 'react';
import classNames from 'classnames';

import { BYTES_IN_MB, MAX_UPLOAD_SIZE_MB } from '~constants/common';

import { Button } from '~components';
import Icon, { faFileUpload } from '~components/icon';

interface ComponentProps {
  allowedExtensions: string[];
  allowedMimeTypes: string[];
  onChange: (file: File) => void;
  maxSizeMB?: number;
  label?: string;
  description?: string;
  className?: string;
  existingFileName?: string | null;
}

const FileUpload: React.FC<ComponentProps> = ({
  onChange,
  allowedExtensions,
  allowedMimeTypes,
  maxSizeMB = MAX_UPLOAD_SIZE_MB,
  label,
  description,
  className,
  existingFileName,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);

  const inputRef = useRef<HTMLInputElement | null>(null);
  const maxBytes = maxSizeMB * BYTES_IN_MB;

  const validate = (file: File): string | true => {
    const ext = `.${file.name.split('.').pop()?.toLowerCase()}`;
    if (!allowedExtensions.includes(ext)) {
      return `Invalid file type: ${ext}`;
    }

    if (!allowedMimeTypes.includes(file.type)) {
      return `Invalid file format: ${file.type}`;
    }

    if (file.size > maxBytes) {
      return `File size exceeds ${maxSizeMB} MB`;
    }

    return true;
  };

  const handleFile = (file: File) => {
    const result = validate(file);
    if (result !== true) {
      setError(result);
      setFileName(null);
      return;
    }

    setError(null);
    setFileName(file.name);
    onChange(file);
  };

  const handleChange = ({ target }: ChangeEvent<HTMLInputElement>) => {
    const file = target.files?.[0];
    if (file) handleFile(file);
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);

    const file = event.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  const acceptAttr = [...allowedExtensions, ...allowedMimeTypes].join(',');

  const containerClass = classNames('file-upload', { 'file-upload--dragging': isDragging }, className);

  return (
    <div className="file-upload-wrapper">
      <div
        className={containerClass}
        onDragOver={(event) => {
          event.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
      >
        <input ref={inputRef} type="file" accept={acceptAttr} style={{ display: 'none' }} onChange={handleChange} />

        <div className="file-upload-content">
          <Icon icon={faFileUpload} size="2x" className="file-upload-icon" />
          <span className="file-upload-label">{label}</span>
          <p className="file-upload-text">{description}</p>

          <Button onClick={() => inputRef.current?.click()} className="file-upload-button">
            Choose File
          </Button>

          {fileName && (
            <div className="file-upload-filename">
              <strong>Selected:</strong> {fileName}
            </div>
          )}

          {!fileName && existingFileName && (
            <div className="file-upload-filename">
              <strong>Selected:</strong> {existingFileName}
            </div>
          )}

          {error && (
            <div className="file-upload-error">
              <span>{error}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FileUpload;
